<template>
  <section
    v-if="errorLogin"
    class="mb-4 rounded border border-primary-400 bg-primary-100 p-4 text-primary-700"
    data-testid="login__login-error-message"
    v-html="myKiKCookie ? $t('You have registered in the myKiK app but do not yet have an account in our online shop? Then we would like to draw your attention to the fact that you also need an existing online shop account for online purchases and only then can you link your myKiK advantage card to your account.') : $t(errorLogin)"
  />
  <form
    class="mb-5 flex flex-col gap-4"
    @submit="onSubmit"
  >
    <div class="mb-3">
      <KikLabel
        label-for="email"
        :label="$t('E-mail address')"
      >
        <SfInput
          id="email"
          v-model="email"
          name="email"
          data-testid="login__email-input"
          type="email"
          :placeholder="$t('E-mail address')"
          v-bind="emailAttrs"
          :invalid="!!errors.email"
        />
      </KikLabel>
      <KikFormError
        class="mt-1"
        :error="errors.email"
      />
    </div>
    <div>
      <div class="relative">
        <KikLabel
          label-for="password"
          :label="$t('Password')"
        >
          <SfInput
            id="password"
            v-model="password"
            name="password"
            data-testid="login__password-input"
            :placeholder="$t('Password')"
            :type="passwordVisible ? 'text' : 'password'"
            v-bind="passwordAttrs"
            :invalid="!!errors.password"
          />
        </KikLabel>
        <div class="absolute bottom-2 right-2 cursor-pointer">
          <Component
            :is="passwordVisible ? SfIconVisibility : SfIconVisibilityOff"
            class="text-neutral-800"
            tabindex="0"
            @keydown.enter="passwordVisible = !passwordVisible"
            @click="passwordVisible = !passwordVisible"
          />
        </div>
      </div>
      <KikFormError
        class="mt-1"
        :error="errors.password"
      />
    </div>

    <div class="mb-4 text-right">
      <SfLink
        class="cursor-pointer text-sm"
        variant="primary"
        data-testid="login__forgotten-password"
        tabindex="0"
        @keydown.enter="$emit('changeView', 'forgotPassword', email)"
        @click="$emit('changeView', 'forgotPassword', email)"
      >
        {{ $t('Forgotten password?') }}
      </SfLink>
    </div>

    <div class="absolute inset-x-0 -bottom-6 me-auto ms-auto w-[320px]">
      <div class="bg-neutral-50 text-center">
        <KikButton
          class="h-[48px] min-w-[280px]"
          data-testid="login__submit-btn"
          tabindex="0"
          :disabled="isSubmitting"
          v-bind="{ type: 'submit' }"
        >
          <SfLoaderCircular
            v-if="isSubmitting"
            size="base"
          />
          <span v-else>{{ $t('Sign in') }}</span>
        </KikButton>
      </div>
    </div>
  </form>
</template>

<script lang="ts" setup>
import {
  SfInput,
  SfIconVisibility,
  SfIconVisibilityOff,
  SfLoaderCircular,
  SfLink,
} from '@storefront-ui/vue';
import * as zod from 'zod';

import { useLoyalty } from '~/composables/custom/useLoyalty/useLoyalty';
import { useTracking } from '~/composables/custom/useTracking';
import { useUser } from '~/composables/custom/useUser/useUser';

defineEmits(['changeView']);

const { error: userError, load, userLogin } = useUser();
const { synchronize: synchronizeUserWithCRM } = useLoyalty();
const route = useRoute();

const {
  isLoginDrawerOpen,
  toggleLoginDrawer,
} = useUiState();

const validationSchema = toTypedSchema(
  zod.object({
    email: zod.string({ message: 'This field is required.' }).min(1, { message: 'This field is required.' }).email({ message: 'The e-mail address is incorrect.' }),
    password: zod.string({ message: 'This field is required.' }).min(1, { message: 'This field is required.' }),
  }),
);

const { defineField, handleSubmit, errors, isSubmitting } = useForm({
  validationSchema,
});

const [email, emailAttrs] = defineField('email');
const [password, passwordAttrs] = defineField('password');

const { trackLogin } = useTracking();
const myKiKCookie = useCookie('mykikapp');

const passwordVisible = ref(false);
const errorLogin = ref();

const deleteMyKiKAppCookie = () => {
  if (myKiKCookie.value) {
    myKiKCookie.value = null;
  }
};

const onSubmit = handleSubmit(async (values) => {
  errorLogin.value = undefined;
  try {
    await userLogin({ username: values.email, password: values.password });
    const hasUserErrors = userError.value.login;
    if (hasUserErrors) {
      errorLogin.value = userError.value.login;
      return;
    }
    deleteMyKiKAppCookie();
    trackLogin();
    await synchronizeUserWithCRM();
    if (isLoginDrawerOpen.value) {
      toggleLoginDrawer();
    }

    await load();
    if (route.name === 'Cart') {
      navigateTo('/checkout/billing-customer');
    }
  }
  catch (e: unknown) {
    if (e instanceof Error) {
      errorLogin.value = e.message;
    }
  }
});
</script>
